import { useMemo } from 'react';
import { map, pipe, prop } from 'ramda';
import { IBonusDetails, IBonusTemplateDetails } from '@core/js/hooks/socket/types';
import { useShowTermsPopup } from '@core/js/components/main/BonusHistory/hooks';

import { useComponentTranslations } from '@core/js/hooks/component';
import { useBonusesFormatter, ICurrentBonusParsed } from '@theme/js/hooks/swiss/useBonusFormatter';
import { getRowCellsCreator } from '@theme/js/components/common/Table/accordionFactory/cellMappers/getRowCellsCreator';
import { getHeaderCellsCreator } from '@theme/js/components/common/Table/accordionFactory/cellMappers/getHeaderCellsCreator';
import style from '../index.scss';
import { getTableAccordionDataCreator } from '@theme/js/components/common/Table/accordionFactory/tableDataCreator';
import { getFormattedData } from '../formatters/getFormattedData';
import { useMyPromotionsCancelBonusPopup } from '@theme/js/hooks/swiss/useMyPromotionsCancelBonusPopup';
import * as actions from '../actions';

type PreWagerBalancesType = {
    balance: IBonusDetails;
    bonusTemplate: IBonusTemplateDetails;
}[]

const PRE_WAGER_SCROLL_KEY = 'PRE_WAGER_SCROLL_KEY';

export const useTableData = (preWagerBalances: PreWagerBalancesType, fetchPreWager: () => Promise<unknown>) => {
    const showTermsPopup = useShowTermsPopup();
    const showCancelBonusPopup = useMyPromotionsCancelBonusPopup({
        actions,
        onSuccess: fetchPreWager,
        key: PRE_WAGER_SCROLL_KEY
    });
    const translate = useComponentTranslations();
    const formattedData = useMemo(() => preWagerBalances?.map(
        ({ balance, bonusTemplate: { gamingContext } }) => ({
            gamingContext,
            ...balance
        })
    ), [preWagerBalances]);

    const bonuses = useBonusesFormatter(formattedData as unknown as ICurrentBonusParsed[]);

    const headerCellsCreator = useMemo(() => getHeaderCellsCreator({
        translate,
        style
    }),
        [translate]
    );

    const rowCellsCreator = useMemo(() => getRowCellsCreator({
        showCancelBonusPopup,
        showTermsPopup,
        style,
        translate
    }), [showCancelBonusPopup, showTermsPopup, translate]);

    const dataFormatter = useMemo(() => getFormattedData(),
        []
    );

    const tableAccordionDataCreator = useMemo(() => getTableAccordionDataCreator({
        pathToId: 'remoteBonusId',
        createHeaderCells: headerCellsCreator,
        createRowCells: pipe(
            dataFormatter,
            data => data.filter(pipe(prop('value'), Boolean)),
            map(rowCellsCreator))
    }), [dataFormatter, headerCellsCreator, rowCellsCreator]);

    return useMemo(() => tableAccordionDataCreator(
        bonuses
    ), [bonuses, tableAccordionDataCreator]);
};
